import { createColumnHelper } from "@tanstack/react-table";

import {
  formatSessionDateLabel,
  formatSessionDateRange,
  formatSessionParticipants,
  formatSessionTimeRange,
  getSessionDisplayTimes,
} from "~/providers/utils/formatSessionDateRange";
import {
  ZoomMeeting,
  ZoomSessionType,
} from "~/providers/utils/types/zoomMeetingTypes";
import { isZoomMeetingJoinable } from "~/providers/utils/zoom/zoomMeetingJoin";
import { SessionDocumentationTags } from "./SessionDocumentationTags";
import { SESSIONS_TABLE_COLUMN_IDS } from "./sessionsTableSorting";
import { SessionTypeSelect } from "./SessionTypeSelect";

export const SESSIONS_JOIN_COLUMN_ID = "join";
export const SESSIONS_TYPE_COLUMN_ID = SESSIONS_TABLE_COLUMN_IDS.type;
const SESSIONS_TRANSCRIPT_COLUMN_ID = "transcript";

const columnHelper = createColumnHelper<ZoomMeeting>();

interface GetSessionsTableColumnsParams {
  onSessionTypeChange: (
    sessionId: string,
    sessionType: ZoomSessionType | null,
  ) => void;
  isUpdatingSessionType?: boolean;
}

export const getSessionsTableColumns = ({
  onSessionTypeChange,
  isUpdatingSessionType = false,
}: GetSessionsTableColumnsParams) => [
  columnHelper.accessor(
    (row) => {
      const { startTime, endTime, duration } = getSessionDisplayTimes(row);
      return formatSessionDateRange(startTime, endTime, duration);
    },
    {
      id: SESSIONS_TABLE_COLUMN_IDS.date,
      header: "Date",
      cell: ({ row }) => {
        const { startTime, endTime, duration } = getSessionDisplayTimes(
          row.original,
        );

        if (!startTime) {
          return "-";
        }

        return (
          <div className="flex flex-col">
            <span>{formatSessionDateLabel(startTime)}</span>
            <span>{formatSessionTimeRange(startTime, endTime, duration)}</span>
          </div>
        );
      },
    },
  ),
  columnHelper.accessor((row) => row.topic ?? "-", {
    id: SESSIONS_TABLE_COLUMN_IDS.topic,
    header: "Topic",
    cell: ({ row }) => row.original.topic ?? "-",
  }),
  columnHelper.accessor(
    (row) => formatSessionParticipants(row.participant_names),
    {
      id: SESSIONS_TABLE_COLUMN_IDS.participants,
      header: "Participants",
      cell: ({ row }) =>
        formatSessionParticipants(row.original.participant_names),
    },
  ),
  columnHelper.display({
    id: SESSIONS_JOIN_COLUMN_ID,
    header: "Join",
    enableSorting: false,
    cell: ({ row }) => {
      const meeting = row.original;

      if (!isZoomMeetingJoinable(meeting)) {
        return "-";
      }

      return (
        <a
          href={meeting.join_url!}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-primary-600 underline underline-offset-4"
          onClick={(event) => event.stopPropagation()}
        >
          Link
        </a>
      );
    },
  }),
  columnHelper.accessor((row) => row.session_type ?? "", {
    id: SESSIONS_TABLE_COLUMN_IDS.type,
    header: "Type",
    cell: ({ row }) => (
      <SessionTypeSelect
        variant="table"
        value={row.original.session_type}
        isDisabled={isUpdatingSessionType}
        onChange={(sessionType) =>
          onSessionTypeChange(row.original.id, sessionType)
        }
      />
    ),
  }),
  columnHelper.display({
    id: SESSIONS_TRANSCRIPT_COLUMN_ID,
    header: "Documentation",
    enableSorting: false,
    cell: ({ row }) => <SessionDocumentationTags session={row.original} />,
  }),
];
